import {
  PURCHASE_ORDER_STATUS_LABELS,
  PURCHASE_ORDER_STATUSES,
  type PurchaseOrder,
  purchaseOrderStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ListPlus, Plus, ShoppingCart } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CounterpartyIdCombobox } from "../../../../features/counterparties/counterparty-combobox.js";
import { moneyExport } from "../../../../features/market/market-values.js";
import { OrderDialog } from "../../../../features/market/order-form.js";
import {
  type PurchaseOrderFilters,
  purchaseOrdersQuery,
  usePurchaseOrders,
} from "../../../../features/market/purchasing-queries.js";
import { useMarketPermissions } from "../../../../features/market/queries.js";
import {
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  PurchaseOrderStatusBadge,
  useMarketStations,
} from "../../../../features/market/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatCount, formatDate, formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  status: purchaseOrderStatusSchema.optional().catch(undefined),
  supplier: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/siparisler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PurchaseOrdersRoute,
});

/**
 * Tedarikçi siparişleri (Prompt 16 — web, aşama B). Taslak → onaya gönder (onay motoru `MARKET_ORDER`; politika
 * yoksa hemen onaylanır) → tedarikçiye gönderildi → mal kabullerle kısmen / tamamen teslim → kapat. Okuma
 * `market.read`, yazma `market.purchase.manage`.
 */
function PurchaseOrdersRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketPurchaseOrders")}
      description={t("market.orders.description")}
      breadcrumb={[{ label: t("menu.marketPurchaseOrders") }]}
    >
      {({ companyId }) => <PurchaseOrdersBody companyId={companyId} />}
    </MarketPage>
  );
}

function PurchaseOrdersBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const station = useMarketStations(companyId);
  const [creating, setCreating] = useState(false);

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: PurchaseOrderFilters = {
    stationId: station.stationId ?? undefined,
    supplierId: search.supplier,
    status: search.status,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = usePurchaseOrders(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<PurchaseOrder>[]>(
    () => [
      {
        id: "orderNo",
        header: t("market.orders.fields.orderNo"),
        hideable: false,
        exportValue: (row) => row.orderNo,
        cell: (row) => (
          <Link
            to="/market/siparisler/$id"
            params={{ id: row.id }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span className="font-mono whitespace-nowrap">{row.orderNo}</span>
            <span className="text-xs text-muted-foreground">{formatDate(row.orderDate)}</span>
          </Link>
        ),
      },
      {
        id: "supplier",
        header: t("market.orders.fields.supplier"),
        exportValue: (row) => row.supplierName,
        cell: (row) => row.supplierName,
      },
      {
        id: "station",
        header: t("market.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => PURCHASE_ORDER_STATUS_LABELS[row.status],
        cell: (row) => <PurchaseOrderStatusBadge status={row.status} />,
      },
      {
        id: "expectedDate",
        header: t("market.orders.fields.expectedDate"),
        exportValue: (row) => row.expectedDate,
        cell: (row) => <span className="tabular">{formatDate(row.expectedDate)}</span>,
      },
      {
        id: "lineCount",
        header: t("market.fields.lineCount"),
        align: "right",
        exportValue: (row) => row.lineCount,
        cell: (row) => <span className="tabular">{formatCount(row.lineCount)}</span>,
      },
      {
        id: "netAmount",
        header: t("market.orders.fields.netAmount"),
        align: "right",
        exportValue: (row) => moneyExport(row.netAmount),
        cell: (row) => <MoneyText value={row.netAmount} className="font-medium" />,
      },
      {
        id: "sentAt",
        header: t("market.orders.fields.sentAt"),
        defaultVisible: false,
        exportValue: (row) => row.sentAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.sentAt)}</span>,
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        <div className="ms-auto flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link
              to="/market/siparis-onerisi"
              search={station.stationId ? { station: station.stationId } : {}}
            >
              <ListPlus aria-hidden="true" />
              {t("menu.marketOrderSuggestions")}
            </Link>
          </Button>
          {permissions.canManagePurchases ? (
            <Button onClick={() => setCreating(true)}>
              <Plus aria-hidden="true" />
              {t("market.orders.new")}
            </Button>
          ) : null}
        </div>
      </Card>

      <DataTable
        caption={t("market.orders.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ShoppingCart />}
            title={t("market.orders.emptyTitle")}
            description={t("market.orders.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("common.status")} className="w-52">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) => setSearch({ status: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {PURCHASE_ORDER_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {PURCHASE_ORDER_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("market.orders.fields.supplier")} className="w-72">
              {(control) => (
                <CounterpartyIdCombobox
                  {...control}
                  companyId={companyId}
                  counterpartyId={search.supplier ?? null}
                  placeholder={t("market.suggestions.allSuppliers")}
                  onChange={(supplierId) => setSearch({ supplier: supplierId ?? undefined })}
                />
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<PurchaseOrder>(
            async (cursor) =>
              (
                await api.GET("/market/purchase-orders", {
                  params: { query: purchaseOrdersQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "tedarikci-siparisleri",
            sheetName: t("menu.marketPurchaseOrders"),
            columns: visible,
            rows,
          });
        }}
      />

      {permissions.canManagePurchases ? (
        <OrderDialog
          open={creating}
          onOpenChange={setCreating}
          companyId={companyId}
          stations={station.stations}
          defaultStationId={station.stationId}
          onSaved={(saved) => void navigate({ to: "/market/siparisler/$id", params: { id: saved.id } })}
        />
      ) : null}
    </>
  );
}
